import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface AsideConfig {
  title: string;
  content: ReactNode;
}

interface LayoutContextValue {
  asideOpen: boolean;
  setAsideOpen: (open: boolean) => void;
  asideTitle: string | null;
  asideContent: ReactNode | null;
  registerAside: (config: AsideConfig | null) => void;
  asideTab: string | null;
  setAsideTab: (tab: string | null) => void;
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function LayoutProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [asideOpen, setAsideOpen] = useState(true);
  const [aside, setAside] = useState<AsideConfig | null>(null);
  const [asideTab, setAsideTab] = useState<string | null>(null);

  const registerAside = useCallback((config: AsideConfig | null) => {
    setAside(config);
    if (config !== null) {
      setAsideOpen(true);
    }
    // NOTE: do not reset ``asideTab`` here. Pages re-register the aside
    // whenever their content props change (e.g. feedback context updates
    // on Report.page); resetting the tab on every re-register would
    // clobber a tab change requested synchronously by the same event
    // that produced the new content.
  }, []);

  const value = useMemo<LayoutContextValue>(
    () => ({
      asideOpen: aside !== null && asideOpen,
      setAsideOpen,
      asideTitle: aside?.title ?? null,
      asideContent: aside?.content ?? null,
      registerAside,
      asideTab,
      setAsideTab,
    }),
    [aside, asideOpen, asideTab, registerAside]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayout(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}

export function useLayoutOptional(): LayoutContextValue | null {
  return useContext(LayoutContext);
}
